import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState({
    downloadsByProject: [],
    recentDownloads: [],
    totalDownloads: 0,
    activeClients: 0
  });
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('all');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await adminAPI.getAnalytics();
      const data = response.data;
      
      // Map API response fields to expected format
      const mappedByProject = (data.byProject || []).map(item => ({
        project_title: item.title,
        download_count: item.downloads
      }));
      
      const mappedRecent = (data.recent || []).map(item => ({
        client_name: item.client_name,
        project_title: item.project_title,
        version: item.version,
        platform: item.platform,
        downloaded_at: item.downloaded_at,
        client_id: item.user_id // Assuming user_id is passed or we generate unique client IDs
      }));
      
      setAnalytics({
        downloadsByProject: mappedByProject,
        recentDownloads: mappedRecent,
        totalDownloads: data.total || mappedRecent.length,
        activeClients: new Set(mappedRecent.map(d => d.client_name)).size
      });
    } catch (error) {
      console.error('Analytics error:', error);
      alert('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMaxDownloads = () => {
    return Math.max(...analytics.downloadsByProject.map(p => p.download_count), 1);
  };

  const getPlatformStats = () => {
    const platforms = {};
    analytics.recentDownloads.forEach(download => {
      platforms[download.platform] = (platforms[download.platform] || 0) + 1;
    });
    return Object.entries(platforms).map(([platform, count]) => ({
      platform,
      count,
      percentage: analytics.totalDownloads > 0 ? (count / analytics.totalDownloads * 100).toFixed(1) : 0
    }));
  };

  if (loading) return <div>Loading...</div>;

  const platformStats = getPlatformStats();
  const maxDownloads = getMaxDownloads();

  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>Analytics & Statistics</h1>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '24px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Downloads</div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>{analytics.totalDownloads}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          padding: '24px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Active Clients</div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>{analytics.activeClients}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          padding: '24px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Projects</div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>{analytics.downloadsByProject.length}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          padding: '24px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Avg per Project</div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>
            {analytics.downloadsByProject.length > 0 
              ? Math.round(analytics.totalDownloads / analytics.downloadsByProject.length)
              : 0}
          </div>
        </div>
      </div>

      <div className="analytics-grid grid-2col" style={{ marginBottom: '24px' }}>
        {/* Downloads by Project */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
            <h2 style={{ margin: 0 }}>Downloads by Project</h2>
          </div>
          <div style={{ padding: '24px' }}>
            {analytics.downloadsByProject.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#718096', padding: '24px' }}>
                No download data available yet
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {analytics.downloadsByProject.map((project, index) => (
                  <div key={index}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>{project.project_title}</span>
                      <span style={{ fontWeight: '700', color: '#667eea' }}>{project.download_count}</span>
                    </div>
                    <div style={{
                      height: '8px',
                      background: '#e2e8f0',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                        width: `${(project.download_count / maxDownloads) * 100}%`,
                        transition: 'width 0.5s'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Platform Distribution */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
            <h2 style={{ margin: 0 }}>Platform Distribution</h2>
          </div>
          <div style={{ padding: '24px' }}>
            {platformStats.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#718096' }}>No data</p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {platformStats.map((stat, index) => (
                  <div key={index}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ 
                        fontWeight: '600', 
                        fontSize: '14px',
                        textTransform: 'capitalize'
                      }}>
                        {stat.platform}
                      </span>
                      <span style={{ fontSize: '14px', color: '#718096' }}>
                        {stat.count} ({stat.percentage}%)
                      </span>
                    </div>
                    <div style={{
                      height: '6px',
                      background: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        background:
                          stat.platform === 'android' ? '#34d399' :
                          stat.platform === 'ios' ? '#60a5fa' :
                          stat.platform === 'web' ? '#fbbf24' : '#a78bfa',
                        width: `${stat.percentage}%`,
                        transition: 'width 0.5s'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Downloads Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <h2 style={{ margin: 0 }}>Recent Downloads</h2>
        </div>
        
        {analytics.recentDownloads.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#718096' }}>
            No downloads yet
          </div>
        ) : (
          <div className="table-responsive">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Client</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Project</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Version</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Platform</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {analytics.recentDownloads.slice(0, 20).map((download, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td data-label="Client" style={{ padding: '16px', fontWeight: '500' }}>{download.client_name}</td>
                  <td data-label="Project" style={{ padding: '16px' }}>{download.project_title}</td>
                  <td data-label="Version" style={{ padding: '16px' }}>v{download.version}</td>
                  <td data-label="Platform" style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 12px',
                      background:
                        download.platform === 'android' ? '#d1fae5' :
                        download.platform === 'ios' ? '#dbeafe' :
                        download.platform === 'web' ? '#fef3c7' : '#e9d5ff',
                      color:
                        download.platform === 'android' ? '#065f46' :
                        download.platform === 'ios' ? '#1e40af' :
                        download.platform === 'web' ? '#92400e' : '#6b21a8',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {download.platform}
                    </span>
                  </td>
                  <td data-label="Date" style={{ padding: '16px', fontSize: '14px' }}>{formatDate(download.downloaded_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
